import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { DND_BASE_LAYER_Z_INDEX } from '../DndEntity/constants';
import EmptyScratchpads from './EmptyScratchpads';
import ScratchpadContent from './ScratchpadContent';
import { useScratchpadContext } from './ScratchpadContext';
import ScratchpadHeader from './ScratchpadHeader';
import { KubitLogoLoader } from '../Chart/ChartLoader';
import { theme } from '../../styles/theme';
import {
  IUserSettingUpdateMutation,
  IUserSettingUpdateMutationVariables,
  UserSettingKind,
} from '../../utils/apollo/graphqlSchema';
import { useMutation } from '@apollo/client';
import useSidebarResizable from '../../utils/hooks/useSidebarResizable';
import { USER_SETTING_UPDATE } from '../../utils/apollo/userSettings';
import ScrollableWrapper from '../ScrollableWrapper';
import ScratchpadModalController from './ScratchpadModalController';
import CollapsableSideBar from '../CollapsableSideBar';
import { clearAllScratchpadAnalysesAction } from '../../redux/slices/analyses';
import { isScratchpadAnalysisId } from '../../redux/utils/transientAnalysis';
import {
  MAX_SCRATCHPAD_WIDTH,
  MIN_SCRATCHPAD_WIDTH,
} from '../../config/constants';

interface IProps {
  isExpanded: boolean;
}

const ResizeHandle = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  width: 4px;
  height: 100%;
  cursor: col-resize;
  background-color: transparent;
  transition: background-color 0.1s;
  z-index: ${DND_BASE_LAYER_Z_INDEX + 4};

  &:hover,
  &:active {
    background-color: ${theme.colors.mainBlue};
  }
`;

const Scratchpad = ({ isExpanded }: IProps) => {
  const dispatch = useAppDispatch();

  const { selectedScratchpad, scratchpads, loading } = useScratchpadContext();
  const sidebarSize = useAppSelector((state) => state.userSettings.sidebarSize);
  const hasSnapshotAnalyses = useAppSelector((state) =>
    Object.keys(state.analyses).some((analysisId) =>
      isScratchpadAnalysisId(analysisId),
    ),
  );

  // Get the current scratchpad title
  const currentScratchpad = scratchpads.find(
    (pad) => pad.id === selectedScratchpad,
  );
  const scratchpadTitle = currentScratchpad?.title || 'New Scratchpad';

  const [saveSettings] = useMutation<
    IUserSettingUpdateMutation,
    IUserSettingUpdateMutationVariables
  >(USER_SETTING_UPDATE);
  const handleWidthChange = useCallback(
    async (newWidth: number) => {
      await saveSettings({
        variables: {
          input: {
            userSettings: [
              {
                kind: UserSettingKind.SidebarSize,
                value: newWidth.toString(),
              },
            ],
          },
        },
      });
    },
    [saveSettings],
  );

  const { width, startResize } = useSidebarResizable({
    minWidth: MIN_SCRATCHPAD_WIDTH,
    maxWidth: MAX_SCRATCHPAD_WIDTH,
    initialWidth: sidebarSize ?? MIN_SCRATCHPAD_WIDTH,
    onResizeEnd: handleWidthChange,
  });

  useEffect(() => {
    return () => {
      // Clean-up scratchpad related analyses
      if (hasSnapshotAnalyses) {
        dispatch(clearAllScratchpadAnalysesAction());
      }
    };
  }, [dispatch, hasSnapshotAnalyses]);

  return (
    <>
      <CollapsableSideBar width={width} isExpanded={isExpanded}>
        {loading ? (
          <KubitLogoLoader height="100%" />
        ) : scratchpads.length === 0 ? (
          <EmptyScratchpads />
        ) : (
          <>
            <ResizeHandle onMouseDown={startResize} />
            <ScratchpadHeader title={scratchpadTitle} />
            {selectedScratchpad && (
              <ScrollableWrapper>
                <ScratchpadContent />
              </ScrollableWrapper>
            )}
          </>
        )}
      </CollapsableSideBar>

      <ScratchpadModalController />
    </>
  );
};

export default Scratchpad;
